import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";

import { KnowledgeOverview } from './KnowledgeOverview';
import { KnowledgeStorage } from './KnowledgeStorage';
import { KnowledgePipelines } from './KnowledgePipelines';
import { KnowledgeEmbeddings } from './KnowledgeEmbeddings';
import { KnowledgeSecurity } from './KnowledgeSecurity';
import { useKnowledge } from '../hooks/useKnowledge';
import { useQueryClient } from '@tanstack/react-query';

const Knowledge = () => {
  const { t } = useTranslation();
  const { data: activeKnowledgeBase } = useKnowledge();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || "overview");
  const { isSuperAdmin } = useIsSuperAdminRole();

  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Refetch dados quando mudar para a aba storage
  useEffect(() => {
    if (activeTab === "storage" && activeKnowledgeBase?.id) {
      console.log("[Knowledge] Tab switched to storage, invalidating storage queries");
      queryClient.invalidateQueries({
        queryKey: ["knowledge-storage", activeKnowledgeBase.id],
      });
    }
  }, [activeTab, activeKnowledgeBase, queryClient]);

  return (
    <div className="space-y-6">
      <Tabs 
        value={activeTab}
        className="w-full"
        onValueChange={(value) => {
          setActiveTab(value);
        }}
      >
        <TabsList>
          <TabsTrigger value="overview">
            {t('knowledge.overview.title')}
          </TabsTrigger>
          <TabsTrigger value="storage">
            {t('knowledge.storage.title')}
          </TabsTrigger>
          <TabsTrigger value="pipelines">
            {t('knowledge.pipelines.title')}
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="embeddings">
              {t('knowledge.embeddings.title')}
            </TabsTrigger>
          )}
          <TabsTrigger value="security">
            {t('knowledge.security.tabTitle')}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="mt-4">
          <KnowledgeOverview />
        </TabsContent>

        <TabsContent value="storage" className="mt-4">
          <KnowledgeStorage />
        </TabsContent>

        <TabsContent value="pipelines" className="mt-4">
          <KnowledgePipelines />
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="embeddings" className="mt-4">
            {activeKnowledgeBase ? (
              <KnowledgeEmbeddings knowledgeBaseId={activeKnowledgeBase.id} />
            ) : (
              <div className="text-center py-4">
                <p>{t('knowledge.embeddings.no_knowledge')}</p>
              </div>
            )}
          </TabsContent>
        )}

        <TabsContent value="security" className="mt-4">
          <KnowledgeSecurity />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Knowledge;
